import React, { useState, useEffect } from 'react';
import { Card, Elevation, Icon, NonIdealState, Spinner, Tag } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import { useAppStore } from '../../stores/UnifiedStoreProvider';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import './Dashboard.css';

interface UserProfile {
  id: string;
  userId: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  avatar?: string;
  title?: string;
}

interface MindMapNode {
  id: string;
  label?: string;
  leadId?: string;
  priority?: string;
  isNecessary?: boolean;
  description?: string;
  parentId?: string;
}

interface PersonCard {
  profile: UserProfile;
  projects: MindMapNode[];  // Root level projects
  totalNodes: number;      // Total nodes they lead
}

const Dashboard: React.FC = () => {
  const [personCards, setPersonCards] = useState<PersonCard[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const appStore = useAppStore();
  const { auth } = appStore;

  useEffect(() => {
    // For now, we'll use mock data or fetch from backend
    // TODO: Implement backend endpoint for dashboard data
    const fetchDashboardData = async () => {
      try {
        setIsLoading(true);
        
        // For now, use mock data
        const mockData: PersonCard[] = [
          {
            profile: {
              id: '1',
              userId: auth.user?.id || '1',
              firstName: auth.user?.name?.split(' ')[0] || 'User',
              lastName: auth.user?.name?.split(' ')[1] || '',
              email: auth.user?.email || 'user@example.com',
              title: 'Team Member'
            },
            projects: [],
            totalNodes: 0
          }
        ];
        
        setPersonCards(mockData);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashboardData();
  }, [auth.user]);

  const handleProjectClick = (projectId: string) => {
    const orgSlug = auth.organization?.slug;
    if (orgSlug) {
      navigate(`/${orgSlug}/mindmap/${projectId}`);
    }
  };

  const getPriorityIcon = (priority?: string) => {
    switch (priority) {
      case 'high':
        return <Icon icon={IconNames.ARROW_UP} intent="danger" size={12} />;
      case 'medium':
        return <Icon icon={IconNames.ARROW_RIGHT} intent="warning" size={12} />;
      case 'low':
        return <Icon icon={IconNames.ARROW_DOWN} intent="success" size={12} />;
      default:
        return null;
    }
  };

  const getInitials = (profile: UserProfile) => {
    const first = profile.firstName?.[0] || '';
    const last = profile.lastName?.[0] || '';
    return (first + last).toUpperCase() || profile.email?.[0]?.toUpperCase() || '?';
  };

  if (isLoading) {
    return (
      <div className="dashboard-container">
        <div className="dashboard-loading">
          <Spinner size={50} />
          <p>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (personCards.length === 0) {
    return (
      <div className="dashboard-container">
        <NonIdealState
          icon={IconNames.PEOPLE}
          title="No Team Members"
          description="No team members with projects found."
        />
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1>Team Dashboard</h1>
        <p className="dashboard-subtitle">Projects and responsibilities by team member</p>
      </div>
      
      <div className="person-cards-grid">
        {personCards.map((personCard) => (
          <Card 
            key={personCard.profile.id} 
            elevation={Elevation.TWO} 
            className="person-card"
            interactive={false}
          >
            <div className="person-header">
              <div className="person-avatar">
                {personCard.profile.avatar ? (
                  <img src={personCard.profile.avatar} alt={getInitials(personCard.profile)} />
                ) : (
                  <div className="avatar-placeholder">
                    {getInitials(personCard.profile)}
                  </div>
                )}
              </div>
              <div className="person-info">
                <h3>
                  {personCard.profile.firstName} {personCard.profile.lastName}
                </h3>
                {personCard.profile.title && (
                  <p className="person-title">{personCard.profile.title}</p>
                )}
                <p className="person-email">{personCard.profile.email}</p>
              </div>
              <div className="person-stats">
                <Tag minimal intent="primary">
                  {personCard.projects.length} Projects
                </Tag>
                <Tag minimal>
                  {personCard.totalNodes} Total Nodes
                </Tag>
              </div>
            </div>
            
            <div className="projects-section">
              <h4>Projects</h4>
              {personCard.projects.length === 0 ? (
                <p className="no-projects">No projects assigned</p>
              ) : (
                <div className="projects-list">
                  {personCard.projects.map((project) => (
                    <Card 
                      key={project.id}
                      className="project-card"
                      interactive
                      onClick={() => handleProjectClick(project.id)}
                    >
                      <div className="project-header">
                        <Icon icon={IconNames.FOLDER_CLOSE} />
                        <span className="project-name">
                          {project.label || 'Untitled Project'}
                        </span>
                        {getPriorityIcon(project.priority)}
                      </div>
                      {project.description && (
                        <p className="project-description">{project.description}</p>
                      )}
                      {project.isNecessary && (
                        <Tag small intent="success">Necessary</Tag>
                      )}
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default Dashboard;